import { useEffect, useRef } from "react";
import { toast } from "sonner";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";

const POLL_MS = 45_000;
const ANNOUNCED_KEY = "dcc.live.announced.v1";

/**
 * Read/write the per-session set of already-announced live-game ids. Uses
 * sessionStorage so the toast fires once per browser session — a soft
 * refresh keeps the state, closing the tab wipes it clean.
 */
function readAnnounced() {
  try {
    const raw = sessionStorage.getItem(ANNOUNCED_KEY);
    if (!raw) return new Set();
    const arr = JSON.parse(raw);
    return new Set(Array.isArray(arr) ? arr : []);
  } catch {
    return new Set();
  }
}

function writeAnnounced(set) {
  try {
    sessionStorage.setItem(ANNOUNCED_KEY, JSON.stringify(Array.from(set)));
  } catch {
    /* ignore quota errors */
  }
}

function pickClubTeamName(game, teamMap, clubIds) {
  const teams = game?.teams || [];
  const clubRef = teams.find((t) => clubIds.has(t?.id));
  const ref = clubRef || teams[0] || null;
  if (!ref) return "Derwent";
  return teamMap[ref.id]?.name || "Derwent";
}

function pickOpponentName(game, teamMap, clubIds) {
  const teams = game?.teams || [];
  const opp = teams.find((t) => !clubIds.has(t?.id)) || teams[1] || null;
  return opp ? (teamMap[opp.id]?.name || "opponent") : "opponent";
}

/**
 * Poll for in-progress Derwent games across the whole app and raise a
 * one-off toast the first time each new game goes live during this
 * session. Meant to be mounted once inside AppShell so member pages get
 * the announcement even when they aren't on the Results screen.
 */
export default function useLiveMatchAnnouncer({ enabled = true } = {}) {
  const seenSetRef = useRef(readAnnounced());
  const firstLoadRef = useRef(true);
  const currentSeasonIdRef = useRef(null);
  const nav = useNavigate();

  useEffect(() => {
    if (!enabled) return undefined;
    let cancelled = false;
    let timer = null;

    const bootstrap = async () => {
      try {
        const { data } = await api.get("/playhq/seasons");
        const seasons = Array.isArray(data) ? data : [];
        const current = seasons.find((s) => s.status && s.status !== "COMPLETED") || seasons[0];
        currentSeasonIdRef.current = current?.playhq_id || null;
      } catch {
        currentSeasonIdRef.current = null;
      }
    };

    const poll = async () => {
      const seasonId = currentSeasonIdRef.current;
      if (!seasonId) return;
      try {
        const { data } = await api.get(`/playhq/seasons/${seasonId}/club-fixtures`, {
          params: { status: "IN_PROGRESS" },
        });
        if (cancelled) return;
        const fixtures = Array.isArray(data?.fixtures) ? data.fixtures : [];
        const teamMap = data?.team_map || {};
        const clubIds = new Set(Array.isArray(data?.club_team_ids) ? data.club_team_ids : []);

        // Preload the first poll's live ids into the seen set *without*
        // announcing — those games were already live when this session
        // began, so a toast would be stale. Every subsequent poll can
        // safely announce anything new.
        if (firstLoadRef.current) {
          for (const f of fixtures) {
            if (f?.playhq_id) seenSetRef.current.add(f.playhq_id);
          }
          writeAnnounced(seenSetRef.current);
          firstLoadRef.current = false;
          return;
        }

        const brandNew = fixtures.filter((f) => f?.playhq_id && !seenSetRef.current.has(f.playhq_id));
        for (const game of brandNew) {
          const derwent = pickClubTeamName(game, teamMap, clubIds);
          const opp = pickOpponentName(game, teamMap, clubIds);
          toast.info(`${derwent} is live now`, {
            id: `live-${game.playhq_id}`,
            description: `Playing ${opp} — tap to open the live scorecard.`,
            duration: 12_000,
            action: {
              label: "Watch",
              onClick: () => nav(`/m/playhq?live=${encodeURIComponent(game.playhq_id)}`),
            },
          });
          seenSetRef.current.add(game.playhq_id);
        }
        if (brandNew.length > 0) writeAnnounced(seenSetRef.current);
      } catch {
        /* silent — the announcer never blocks navigation */
      }
    };

    const start = async () => {
      await bootstrap();
      if (cancelled) return;
      await poll();
      timer = setInterval(poll, POLL_MS);
    };
    start();

    return () => {
      cancelled = true;
      if (timer) clearInterval(timer);
    };
  }, [enabled, nav]);
}
